import React from "react";
import { useNavigate } from "react-router-dom";
import "./CollegeStudents.css";

export default function CollegeStudents() {
  const navigate = useNavigate();

  const collegeRoadmap = [
    {
      topic: "Linux",
      percentage: 5,
      startDay: 1,
      endDay: 12,
      totalDays: 12,
      focus:
        "Terminal basics, file system, commands, permissions, process basics, and developer workflow.",
      checkpoints: [
        "Linux command assignment",
        "Terminal practice task",
        "Basic environment setup review",
      ],
    },
    {
      topic: "HTML",
      percentage: 6,
      startDay: 13,
      endDay: 36,
      totalDays: 24,
      focus:
        "HTML structure, semantic tags, forms, tables, media, SEO basics, and page structure.",
      checkpoints: ["HTML page assignment", "Resume webpage mini project", "Structure review"],
    },
    {
      topic: "CSS",
      percentage: 6,
      startDay: 37,
      endDay: 60,
      totalDays: 24,
      focus:
        "Selectors, box model, flexbox, grid, responsive design, animations, and modern UI styling.",
      checkpoints: ["Landing page assignment", "Responsive layout project", "UI review session"],
    },
    {
      topic: "Python",
      percentage: 11,
      startDay: 61,
      endDay: 114,
      totalDays: 54,
      focus:
        "Python fundamentals, functions, OOP, files, error handling, modules, scripting, and problem solving.",
      checkpoints: ["Python practice assignments", "Automation mini project", "Coding mock discussion"],
    },
    {
      topic: "FastAPI",
      percentage: 5,
      startDay: 115,
      endDay: 138,
      totalDays: 24,
      focus:
        "API creation, routing, request/response models, validation, authentication basics, and backend structure.",
      checkpoints: ["FastAPI assignment", "CRUD API mini project", "API testing review"],
    },
    {
      topic: "JavaScript",
      percentage: 5,
      startDay: 139,
      endDay: 162,
      totalDays: 24,
      focus:
        "Variables, functions, DOM, events, async JavaScript, fetch API, and browser logic.",
      checkpoints: ["JavaScript assignment", "DOM mini project", "Frontend logic review"],
    },
    {
      topic: "React.js",
      percentage: 9,
      startDay: 163,
      endDay: 202,
      totalDays: 40,
      focus:
        "Components, props, state, hooks, routing, API integration, forms, and frontend project building.",
      checkpoints: ["React assignment", "Frontend project", "Mock project explanation"],
    },
    {
      topic: "MongoDB",
      percentage: 5,
      startDay: 203,
      endDay: 216,
      totalDays: 14,
      focus:
        "Collections, documents, CRUD, schema design basics, aggregation basics, and backend integration.",
      checkpoints: ["MongoDB CRUD assignment", "Database integration task", "Data model review"],
    },
    {
      topic: "Docker",
      percentage: 6,
      startDay: 217,
      endDay: 244,
      totalDays: 28,
      focus:
        "Containers, images, Dockerfile, volumes, networks, compose basics, and deployment mindset.",
      checkpoints: ["Docker assignment", "Containerize project", "Deployment review"],
    },
    {
      topic: "Kubernetes",
      percentage: 6,
      startDay: 245,
      endDay: 272,
      totalDays: 28,
      focus:
        "Pods, deployments, services, scaling basics, config, and production system understanding.",
      checkpoints: ["Kubernetes basics task", "Deployment YAML practice", "Infra mock discussion"],
    },
    {
      topic: "GitHub",
      percentage: 5,
      startDay: 273,
      endDay: 286,
      totalDays: 14,
      focus:
        "Repositories, pull requests, README writing, project hosting, and portfolio presentation.",
      checkpoints: ["GitHub portfolio setup", "README review", "Profile improvement"],
    },
    {
      topic: "Artificial Intelligence",
      percentage: 16,
      startDay: 287,
      endDay: 360,
      totalDays: 74,
      focus:
        "AI fundamentals, machine learning, deep learning, GenAI, LLMs, NLP, computer vision, model usage, and AI project building.",
      checkpoints: [
        "AI assignment",
        "ML/AI mini project",
        "GenAI project",
        "AI project presentation",
        "Technical mock interview",
      ],
    }
  ];

  const dailyRoutine = [
    { time: "7:00 AM – 7:30 AM", task: "Concept Revision / Coding Warm-up", icon: "💻", type: "Preparation" },
    { time: "7:30 AM – 8:00 AM", task: "Breakfast + College Preparation", icon: "🌅", type: "Life Balance" },
    { time: "8:00 AM – 5:00 PM", task: "College / Degree Lectures / Practical Work", icon: "🏫", type: "College Time" },
    { time: "5:00 PM – 6:00 PM", task: "Commute / Rest / Refresh", icon: "☕", type: "Life Balance" },
    { time: "6:00 PM – 7:00 PM", task: "College Assignment / Semester Work", icon: "📚", type: "College Time" },
    { time: "7:00 PM – 8:15 PM", task: "StudyAIPlacement Assignment / Guided Learning", icon: "📌", type: "Preparation" },
    { time: "8:15 PM – 9:15 PM", task: "Project Building / Coding Practice", icon: "🚀", type: "Preparation" },
    { time: "9:15 PM – 9:45 PM", task: "Doubt Solving / Peer Discussion / Revision", icon: "🔁", type: "Preparation" },
    { time: "9:45 PM – 10:00 PM", task: "Next-Day Planning + Sleep Preparation", icon: "🌙", type: "Life Balance" },
  ];

  const everyTopicIncludes = [
    "Guided lecture learning and study flow",
    "Notes building and daily revision",
    "Assignments after topic completion",
    "Doubt solving and discussion rounds",
    "Mini projects and implementation tasks",
    "Weekend review and progress tracking",
    "Mock explanation or interview practice",
    "GitHub / portfolio update wherever applicable",
  ];

  const preparationTasks = [
    {
      icon: "🎓",
      name: "Guided Lecture Session",
      text: "Structured lecture study, mentor-guided topic flow, and concept understanding.",
    },
    {
      icon: "📝",
      name: "Notes Building",
      text: "Writing important points, examples, commands, syntax, mistakes, and interview notes.",
    },
    {
      icon: "🔁",
      name: "Concept Revision",
      text: "Revising previous lectures, notes, weak areas, assignments, and important concepts.",
    },
    {
      icon: "💻",
      name: "Coding Practice",
      text: "Hands-on coding, examples, implementation, debugging, and regular practice tasks.",
    },
    {
      icon: "📌",
      name: "Assignment Work",
      text: "Completing assigned tasks, topic exercises, submissions, and improvement work.",
    },
    {
      icon: "🚀",
      name: "Project Building",
      text: "Building portfolio projects, GitHub updates, deployment work, and project explanation.",
    },
    {
      icon: "❓",
      name: "Doubt Solving",
      text: "Clearing pending doubts with mentor or team support during available guidance windows.",
    },
    {
      icon: "🤝",
      name: "Peer Discussion",
      text: "Discussion with other learners, explanation practice, group learning, and confidence building.",
    },
  ];

  const lifeBalanceTasks = [
    {
      icon: "🏫",
      name: "College Time",
      text: "College lectures, practicals, semester assignments, exams, and academic responsibilities are protected.",
    },
    {
      icon: "🌅",
      name: "Morning Preparation",
      text: "Breakfast, college preparation, planning, and routine setup before college hours.",
    },
    {
      icon: "☕",
      name: "Commute + Refresh",
      text: "Time to return from college, rest, refresh, and prepare for evening study blocks.",
    },
    {
      icon: "🌙",
      name: "Sleep Discipline",
      text: "Early sleep planning to protect the next morning study routine.",
    },
  ];

  const jobSprint = [
    {
      title: "Career Positioning",
      image: "/placement/positioning.png",
      items: [
        "Identify suitable roles based on skills and degree background",
        "Choose target direction: AI, Full Stack, Data, Backend, or GenAI",
        "Create a strong fresher career story for recruiters",
        "Fix weak points before serious internship and job applications",
      ],
    },
    {
      title: "Resume, LinkedIn & GitHub Upgrade",
      image: "/placement/resume-linkedin.png",
      items: [
        "Prepare ATS-friendly fresher resume",
        "Highlight projects, internships, college work, and technical skills",
        "Polish LinkedIn headline, about section, and profile",
        "Clean GitHub with proper README and project links",
      ],
    },
    {
      title: "Project Explanation Confidence",
      image: "/placement/project-demo.png",
      items: [
        "Practice explaining major projects clearly",
        "Prepare architecture and workflow explanation",
        "Build answers for challenges, bugs, and improvements",
        "Create demo-ready project presentation flow",
      ],
    },
    {
      title: "Technical Interview Preparation",
      image: "/placement/technical-interview.png",
      items: [
        "Revise core subjects based on target role",
        "Practice Python, JavaScript, React, backend, database, and AI questions",
        "Attend project-based technical mock interviews",
        "Correct weak areas after every mock round",
      ],
    },
    {
      title: "HR & Communication Training",
      image: "/placement/hr-communication.png",
      items: [
        "Prepare strong self-introduction",
        "Explain academic background and project journey clearly",
        "Practice salary expectation discussion",
        "Prepare HR, behavioural, and final round answers",
      ],
    },
    {
      title: "Internship & Job Application Strategy",
      image: "/placement/job-application.png",
      items: [
        "Select target internships, fresher jobs, and trainee roles",
        "Track daily and weekly job applications",
        "Prepare recruiter messages and emails",
        "Use LinkedIn, Naukri, company career pages, and referrals",
      ],
    },
    {
      title: "Mock Interviews & Panel Reviews",
      image: "/placement/mock-interview.png",
      items: [
        "Regular mock interview practice",
        "Panel discussion and explanation rounds",
        "Resume walkthrough practice",
        "Feedback-based improvement plan after each session",
      ],
    },
    {
      title: "Final Push & Follow-up",
      image: "/placement/final-push.png",
      items: [
        "Analyze interview feedback",
        "Plan follow-up message strategy",
        "Discuss offer and next steps",
        "Continue support until the student becomes career-ready",
      ],
    },
  ];

  return (
    <main className="C-college-page">
      {/* HERO */}
      <section className="C-college-hero">
        <div className="C-college-hero-content">
          <p className="section-kicker">COLLEGE STUDENTS ROADMAP</p>

          <h1>A 2-year career-building roadmap for college students.</h1>

          <p>
            Continue your college degree while building AI, Full Stack, Data Science,
            GenAI, projects, portfolio, internship readiness, and interview confidence
            through a disciplined morning and evening study system.
          </p>

          <div className="C-salary-band">
            <p className="section-kicker C-salary-kicker">Target preparation range</p>
            <strong>Up to ₹40 LPA</strong>
            <small>
              Based on skill, consistency, project quality, internship exposure,
              interview performance, market conditions, role fit, and career readiness.
            </small>
          </div>

          <div className="C-college-hero-actions">
            <button onClick={() => navigate("/counselling?roadmap=college-students")}>
              Apply for Counselling
            </button>
            <a href="#college-roadmap">View 12-Month Roadmap</a>
          </div>
        </div>
      </section>

      {/* PRINCIPLE */}
      <section className="C-college-section">
        <div className="C-college-principle-box">
          <div>
            <p className="section-kicker">COLLEGE + CAREER DISCIPLINE PRINCIPLE</p>

            <h2>
              College students have time. We convert that time into career advantage.
            </h2>

            <p>
              The goal is not only to complete a degree. The goal is to use college years
              properly: attend guided lecture sessions, revise, code, complete assignments,
              build projects, clear doubts, improve communication, and prepare for
              internships and strong technology opportunities step-by-step.
            </p>
          </div>

          <div className="C-mentor-clarity-card">
            <h3>Guided, not confused.</h3>

            <p>
              College students often do not know what to learn, when to learn, and how
              to prepare for real industry expectations. B. Akshay personally guides them
              on career direction, AI industry clarity, discipline, study mindset, and
              important high-level doubts.
            </p>

            <p>
              Along with mentor guidance, the StudyAIPlacement team supports students through
              assignments, projects, mock interviews, panel discussions, resume reviews,
              LinkedIn/GitHub improvement, and placement preparation.
            </p>

            <p>
              The goal is to help serious college students become career-ready before
              graduation, not after graduation panic starts.
            </p>
          </div>
        </div>
      </section>

      {/* DAILY TASK SYSTEM */}
      <section className="C-college-section">
        <div className="C-daily-task-system">
          <div className="C-college-heading C-compact">
            <p className="section-kicker">DAILY TASK SYSTEM</p>

            <h2>College plus skill-building. Both need a clear daily system.</h2>

            <p>
              College students follow a focused morning and evening study system. The day
              is divided into Guided Lecture Sessions, Notes Building, Revision, Coding
              Practice, Assignment Work, Project Building, Doubt Solving, and Peer
              Discussion while keeping college hours protected.
            </p>
          </div>

          <div className="C-task-system-columns">
            <div className="C-task-system-panel">
              <h3>Preparation related tasks</h3>

              <div className="C-task-chip-grid">
                {preparationTasks.map((task) => (
                  <div className="C-task-chip-card" key={task.name}>
                    <div className="C-task-icon">{task.icon}</div>
                    <span>{task.name}</span>
                    <p>{task.text}</p>
                  </div>
                ))}
              </div>
            </div>

            <div className="C-task-system-panel C-life">
              <h3>College and life balance</h3>

              <div className="C-task-chip-grid">
                {lifeBalanceTasks.map((task) => (
                  <div className="C-task-chip-card" key={task.name}>
                    <div className="C-task-icon">{task.icon}</div>
                    <span>{task.name}</span>
                    <p>{task.text}</p>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* DAILY ROUTINE */}
      <section className="C-college-section">
        <div className="C-college-heading">
          <p className="section-kicker">COLLEGE + STUDY DAILY ROUTINE</p>

          <h2>A practical routine for students already attending college.</h2>

          <p>
            The timetable keeps college hours protected while using morning and evening
            blocks for skill-building. Students study through lecture sessions, revision,
            coding, assignments, projects, doubts, peer discussion, and next-day planning.
          </p>
        </div>

        <div className="C-routine-grid">
          {dailyRoutine.map((item) => (
            <div
              className={`C-routine-card ${item.type === "College Time"
                ? "C-college-routine-card"
                : item.type === "Life Balance"
                  ? "C-life-routine-card"
                  : "C-prep-routine-card"
                }`}
              key={item.time}
            >
              <div className="C-routine-card-top">
                <span>{item.time}</span>
                <small>{item.type}</small>
              </div>

              <div className="C-routine-task-line">
                <b>{item.icon}</b>
                <p>{item.task}</p>
              </div>
            </div>
          ))}
        </div>

        <div className="C-weekend-system">
          <div>
            <strong>Saturday focused review</strong>
            <p>
              Weekly revision, assignment review, project checkpoint, panel discussion,
              mock interview, and progress tracking.
            </p>
          </div>

          <div>
            <strong>Sunday recovery + light revision</strong>
            <p>
              Personal balance, college work, family time, and optional light revision so
              students can stay consistent for the long journey.
            </p>
          </div>
        </div>
      </section>

      {/* ROADMAP */}
      <section className="C-college-section" id="college-roadmap">
        <div className="C-college-heading">
          <p className="section-kicker">UP TO 12-MONTH SKILL ROADMAP</p>

          <h2>Every phase builds career readiness before graduation.</h2>

          <p>
            This 12-month roadmap gives college students enough time to build strong
            fundamentals, frontend, backend, databases, DevOps, mobile development,
            AI, Data Science, GenAI, projects, portfolio, internship readiness, and
            interview confidence while continuing their degree.
          </p>
        </div>

        <div className="C-roadmap-unlock-list">
          {collegeRoadmap.map((item, index) => (
            <div className="C-unlock-card" key={item.topic}>
              <div className="C-unlock-top">
                <span>Phase {String(index + 1).padStart(2, "0")}</span>
                <b>{item.percentage}%</b>
              </div>

              <h3>{item.topic}</h3>

              <div className="C-day-pill">
                Day {item.startDay} – Day {item.endDay}
                <small>{item.totalDays} days</small>
              </div>

              <p>{item.focus}</p>

              <div className="C-checkpoint-list">
                {item.checkpoints.map((checkpoint) => (
                  <span key={checkpoint}>{checkpoint}</span>
                ))}
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* EVERY TOPIC */}
      <section className="C-college-section">
        <div className="C-topic-system-box">
          <div className="C-college-heading C-compact">
            <p className="section-kicker">INSIDE EVERY TOPIC</p>

            <h2>Students do not just watch lectures. They build, practice, and improve.</h2>
          </div>

          <div className="C-topic-system-grid">
            {everyTopicIncludes.map((item, index) => (
              <div className="C-topic-system-card" key={item}>
                <span>{String(index + 1).padStart(2, "0")}</span>
                <p>{item}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* COLLEGE ADVANTAGE */}
      <section className="C-college-section">
        <div className="C-topic-system-box">
          <div className="C-college-heading C-compact">
            <p className="section-kicker">COLLEGE ADVANTAGE TRACK</p>

            <h2>Students can build academic and industry proof before graduation.</h2>

            <p>
              College years are a powerful time to build more than coding skills. Students
              can work on research papers, conferences, hackathons, final-year projects,
              technical writing, presentations, internships, and portfolio readiness.
            </p>
          </div>

          <div className="C-topic-system-grid">
            <div className="C-topic-system-card">
              <span>01</span>
              <p>Research paper topic selection and writing guidance</p>
            </div>

            <div className="C-topic-system-card">
              <span>02</span>
              <p>Conference participation and paper presentation support</p>
            </div>

            <div className="C-topic-system-card">
              <span>03</span>
              <p>Hackathon preparation, prototype building, and demo practice</p>
            </div>

            <div className="C-topic-system-card">
              <span>04</span>
              <p>Final-year project guidance with industry-level execution</p>
            </div>

            <div className="C-topic-system-card">
              <span>05</span>
              <p>Technical blog, README, report, and documentation writing</p>
            </div>

            <div className="C-topic-system-card">
              <span>06</span>
              <p>Seminar, viva, PPT, and project presentation practice</p>
            </div>

            <div className="C-topic-system-card">
              <span>07</span>
              <p>Internship preparation before final year</p>
            </div>

            <div className="C-topic-system-card">
              <span>08</span>
              <p>GitHub, LinkedIn, resume, and portfolio building before graduation</p>
            </div>
          </div>
        </div>
      </section>

      {/* JOB PREP */}
      <section className="C-college-section C-placement-journey-section">
        <div className="C-college-heading">
          <p className="section-kicker">AFTER THE 12-MONTH ROADMAP</p>

          <h2>
            From college student to career-ready candidate, students follow a complete
            placement journey.
          </h2>

          <p>
            Career support is not limited to resume writing. College students move
            through profile positioning, resume, LinkedIn, GitHub, project explanation,
            mock interviews, HR preparation, recruiter strategy, feedback, and final
            internship or placement readiness.
          </p>
        </div>

        <div className="C-placement-journey">
          {jobSprint.map((phase, index) => (
            <div className="C-placement-step" key={phase.title}>
              <div className="C-placement-step-number">
                {String(index + 1).padStart(2, "0")}
              </div>

              <div className="C-placement-step-content">
                <div className="C-placement-visual">
                  <img src={phase.image} alt={phase.title} />
                </div>

                <div className="C-placement-text">
                  <span>Placement Step {index + 1}</span>
                  <h3>{phase.title}</h3>

                  <ul>
                    {phase.items.map((item) => (
                      <li key={item}>{item}</li>
                    ))}
                  </ul>
                </div>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* OUTCOME */}
      <section className="C-college-section">
        <div className="C-outcome-box">
          <p className="section-kicker">WHAT STUDENTS BUILD</p>

          <h2>Students finish with career-ready proof of skill.</h2>

          <p>
            By the end of the roadmap, serious college students are expected to build
            visible career assets that support internships, fresher jobs, campus
            placements, and stronger AI, software, data, and full stack opportunities.
          </p>

          <div className="C-outcome-grid">
            <span>Research paper writing confidence</span>
            <span>Conference and paper presentation readiness</span>
            <span>Hackathon participation proof</span>
            <span>Final-year project excellence</span>
            <span>Technical blog and documentation writing</span>
            <span>Internship readiness before graduation</span>
            <span>AI + Full Stack project portfolio</span>
            <span>GitHub profile with clean repositories</span>
            <span>Resume and LinkedIn readiness</span>
            <span>Deployed web applications</span>
            <span>Backend APIs with database integration</span>
            <span>AI / ML / GenAI project demos</span>
            <span>Project explanation confidence</span>
            <span>Interview and HR readiness</span>
            <span>Internship application readiness</span>
            <span>Campus placement preparation</span>
            <span>Fresher job application readiness</span>
            <span>Long-term career direction</span>
          </div>

          <button onClick={() => navigate("/counselling?roadmap=college-students")}>
            Apply for Counselling
          </button>
        </div>
      </section>
    </main>
  );
}